import React, { useState } from 'react';
// @ts-ignore
import Fade from '@stahl.luke/react-reveal/Fade';
import { Tab } from '@headlessui/react';
import { PlayIcon } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LandingNavigation from '../LandingNavigation';
import mainScreenBg from '../what-is-nucleo/assets/logoPart.svg';
import Footer from '../Footer';
import MeetTheTeamTab from './MeetTheTeamTab';
import YoutubeVideo from '../../post/youtube-video/YoutubeVideo';
import FullscreenPopup from '../../../elements/FullscreenPopup';
import { useDocumentTitle } from '../../../helpers/hooks';

const introVideoUrl = 'https://www.youtube-nocookie.com/embed/0qQqIgZIR-E';
const walkthroughVideoUrl = 'https://www.youtube-nocookie.com/embed/LY2Qk-4abtY';

const aboutVideos = [
  {
    title: 'Introducing Nucleo',
    description: 'A short introduction to Nucleo and what we are building for Life Science professionals.',
    url: introVideoUrl,
  },
  {
    title: 'Platform walkthrough',
    description: 'A quick tour of the Nucleo platform and how to make the most of it.',
    url: walkthroughVideoUrl,
  },
];

export default function AboutNucleo() {

  useDocumentTitle('About');

  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);

  return (
    <main className="what-is-nucleo-page bg-neutral-50 relative bg-white overflow-hidden">
      <section className="overflow-hidden md:overflow-visible relative flex justify-center pb-8 md:pb-0">
        <LandingNavigation />

        <img
          alt=""
          src={mainScreenBg}
          className="absolute top-0 -right-44 md:right-0 transform 2xl:scale-150 pointer-events-none"
        />

        <Fade left>
          <div className="mt-24 xl:mt-0 z-10 w-full flex items-center min-h-[320px] md:min-h-[400px] max-w-6xl w-full px-4 md:px-6 xl:px-0">
            <div className="backdrop-blur-md w-full bg-white/90 md:w-auto px-8 py-12 rounded-md xl:p-0 xl:bg-transparent xl:backdrop-filter-none">
              <p className="text-main-900 font-extrabold text-4xl sm:text-5xl md:text-5.5xl">
                About Nucleo
              </p>
              <p className="text-neutral-700 text-base font-medium mt-6" style={{ maxWidth: 530 }}>
                Watch our intro and platform walkthrough, then dive into the story and meet the team.
              </p>
            </div>
          </div>
        </Fade>
      </section>

      <section className="relative pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 xl:px-0">
          <Fade left>
            <div className="flex flex-col gap-12 md:gap-16 max-w-2xl">
              {aboutVideos.map((video) => (
                <div key={video.title}>
                  <h3 className="font-bold text-main-900 text-2xl">
                    {video.title}
                  </h3>
                  <p className="text-neutral-700 text-base font-medium mt-2" style={{ maxWidth: 530 }}>
                    {video.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveVideoUrl(video.url)}
                    className="mt-4 inline-flex items-center text-base font-semibold p-4 rounded-md border border-main-600 bg-main-600 text-white hover:bg-main-800 transition"
                  >
                    <PlayIcon className="w-4 h-4 mr-2" />
                    Watch the video
                  </button>
                </div>
              ))}
            </div>
          </Fade>
        </div>
      </section>

      <section className="relative pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 xl:px-0">
          <Fade left>
            <Tab.Group>
              <Tab.List className="flex flex-col sm:flex-row gap-4">
                <Tab className={({ selected }) => `text-main-900 p-4 rounded-md ${selected ? 'font-bold bg-white shadow-md' : 'bg-main-50'}`}>
                  Why are we doing this?
                </Tab>
                <Tab className={({ selected }) => `text-main-900 p-4 rounded-md ${selected ? 'font-bold bg-white shadow-md' : 'bg-main-50'}`}>
                  The story
                </Tab>
                <Tab className={({ selected }) => `text-main-900 p-4 rounded-md ${selected ? 'font-bold bg-white shadow-md' : 'bg-main-50'}`}>
                  Meet the Team
                </Tab>
              </Tab.List>

              <Tab.Panels>
                <Tab.Panel style={{ maxWidth: 560 }}>
                  <h2 className="mt-6 text-3xl text-main-900 font-extrabold">Why are we doing this?</h2>
                  <p className="text-neutral-700 font-medium mt-6">
                    How do I get to see the full product landscape for in-licensing opportunities in my markets?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    How do I find the right regulatory agent in the Middle East for my company?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    How do I find a new manufacturer of soft gel capsules with EU GMP approval?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    How do I connect with the right person in an organization?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    How do I talk to my peers facing the same issues in the life science industry?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    How do I build a relevant and credible network in this industry?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    Where do I go for real-time data from the company itself as opposed to outdated or inaccurate information somewhere on the internet?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    We could not easily find answers to these questions; this is why we developed Nucleo.
                  </p>
                </Tab.Panel>

                <Tab.Panel style={{ maxWidth: 560 }}>
                  <h2 className="mt-6 text-3xl text-main-900 font-extrabold">The story</h2>
                  <p className="text-neutral-700 font-medium mt-6">
                    In spring 2020, in the middle of the first Covid wave, I was asked by a young pharma professional how I had built my network. How did I know who to go to in the life sciences industry for the right product for a market, for the right expert in a particular field?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    The simple answer was over the last 30 years the majority of my time was spent attending conferences, and networking with business colleagues to seek out the right contact.
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    While this has often worked, it’s far from perfect. Nobody’s network is exhaustive. I know what I know and, more importantly, who I know but that’s it. I don’t know what or who I don’t know.
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    The life sciences landscape is certainly not infinite, yet organisations are out there just looking to network with the right partner. We spend precious time using existing databases or sending unsolicited messages hoping to make a connection and potentially land a deal.
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    We’re all attending conferences trying to find that elusive product or service, and none of this is secret information. If we are offering products and services in life sciences, we want the world to know it.
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    Don’t get me wrong, face-to-face meetings are still the best way to build partnerships. But most of us are using valuable time and money attending conferences and fairs hoping to find potential contacts.
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    Wouldn’t it be better to know who we wanted to meet before we got there?
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    Sadly, even with the advancements and innovation in product development, the life science industry continues to do business like we are living in 1976 (and yes, I remember living in 1976!).
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    It’s time to evolve. In order to do that, we must bring efficiency to business development and make connections more quickly. Let’s get to the fun part and do the deals. Let’s skip the trailer and go directly to the feature film.
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    Nucleo is at the centre of what’s new and exciting. We are the answer to what’s missing in life sciences. We are here to inject energy into the world of Pharma and make prosperous connections that matter; now and beyond.
                  </p>
                  <p className="text-neutral-700 font-medium mt-6">
                    Ewan Livesey, Co-Founder/CEO Nucleo
                  </p>
                </Tab.Panel>

                <MeetTheTeamTab />
              </Tab.Panels>
            </Tab.Group>
          </Fade>
        </div>
      </section>

      <div className="relative overflow-hidden">
        <Footer />
      </div>

      <FullscreenPopup
        open={activeVideoUrl !== null}
        setOpen={(flag) => { if (!flag) setActiveVideoUrl(null); }}
      >
        <div
          className="fixed inset-0 flex items-center justify-center bg-neutral-900/90 px-4 md:px-12 py-12"
          role="presentation"
          onClick={() => setActiveVideoUrl(null)}
        >
          <div
            className="relative w-full max-w-5xl"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveVideoUrl(null)}
              className="absolute -top-12 right-0 text-white hover:text-neutral-300 focus:outline-none"
              aria-label="Close video"
            >
              <XMarkIcon className="h-8 w-8" />
            </button>
            <YoutubeVideo url={activeVideoUrl !== null ? `${activeVideoUrl}?autoplay=1` : ''} />
          </div>
        </div>
      </FullscreenPopup>
    </main>
  );
}
